import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SizeMe } from 'react-sizeme';
import styled from 'styled-components';

import { colors, media } from '../../../core';
import Icon from '../Icon';
import Link from '../Link';
import Logo from '../Logo';
import { default as UserDropdown, IconButton, Menu, UserProps } from '../UserDropdown';

export interface NavMenuProps {
  open?: boolean;
}

export interface LinkProps {
  url: string;
  label: string;
}

interface NavbarProps {
  onLogoClick: () => void;
  user: UserProps;
  showReferrals?: boolean;
}

const Bar = styled.header`
  background-color: ${colors.white};
  box-shadow: 0 2px 4px 0 rgba(215, 215, 215, 0.5);
  position: relative;
  width: 100%;
  z-index: 1000;
`;

const InnerBar = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin: 0 auto;
  max-width: 1440px;
  padding: 20px;
  justify-content: space-between;

  ${media.tabletPortrait} {
    padding: 20px 12px;
  }
`;

const Nav = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const NavMainLink = styled(Link)`
  color: ${({ theme }) => theme.navbarColor || colors.charcoal};
  padding: 4px 48px 0 0;
  margin: 0;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
  &:focus {
    box-shadow: none;
  }

  ${media.small} {
    padding-right: 24px;
  }

  ${media.tabletPortrait} {
    display: block;
    font-size: 16px;
    padding: 2.2vh 12px;
  }
`;

const MenuIcon = styled(Icon)`
  color: ${({ theme }) => theme.navbarColor || colors.primary};
  cursor: pointer;
`;

const Navbar = ({ onLogoClick, user, showReferrals = false }: NavbarProps) => {
  const ref = useRef<any>(null);
  const [isNavOpen, setNavOpen] = useState(false);

  const handleNavAnchorClick = useCallback(() => {
    setNavOpen(!isNavOpen);
  }, [isNavOpen]);

  const handleEscape = useCallback((keyboardEvent) => {
    if (keyboardEvent.key === 'Escape') {
      setNavOpen(false);
    }
  }, []);

  const handleClickOutside = useCallback((clickEvent) => {
    if (ref && ref.current && !ref.current.contains(clickEvent.target)) {
      setNavOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keyup', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keyup', handleEscape);
    };
  }, [handleEscape, handleClickOutside]);

  return (
    <SizeMe>
      {({ size }) => (
        <Bar>
          <InnerBar>
            <Logo onClick={onLogoClick} />
            <Nav ref={ref}>
              {size.width && size.width >= 768 && (
                <React.Fragment>
                  {showReferrals && <NavMainLink href="/user/referrals">Referrals</NavMainLink>}
                  <NavMainLink href="/booking/package">Book Now</NavMainLink>
                  <NavMainLink href="/">Prenuvo Website</NavMainLink>
                  <UserDropdown
                    isNavOpen={isNavOpen}
                    user={user}
                    handleNavAnchorClick={handleNavAnchorClick}
                    showUnderline={true}
                  />
                </React.Fragment>
              )}

              {size.width && size.width < 768 && (
                <React.Fragment>
                  <IconButton onClick={handleNavAnchorClick}>
                    <MenuIcon type="hamburger" />
                  </IconButton>

                  <Menu isNavOpen={isNavOpen} user={user} onClose={() => setNavOpen(false)}>
                    {showReferrals && <NavMainLink href="/user/referrals">Referrals</NavMainLink>}
                    <NavMainLink href="/booking/package">Book Now</NavMainLink>
                    <NavMainLink href="/">Prenuvo Website</NavMainLink>
                  </Menu>
                </React.Fragment>
              )}
            </Nav>
          </InnerBar>
        </Bar>
      )}
    </SizeMe>
  );
};

export default Navbar;
